import { Fetcher } from '@this/src/util';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import _ from 'lodash';
import type { Moment } from 'moment';
import Notification from '@this/src/notification';
import type { HotelOrderItemEanTodoResponseArgs } from '@this/src/domain/arrangement/hotel_order_item_ean_todo';
import HotelOrderItemEanTodo from '@this/src/domain/arrangement/hotel_order_item_ean_todo';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import type { OrderItemJobTypeKey } from '@this/src/domain/order_item/order_item_job_type';
import { OrderItemJobTypeNumKeys } from '@this/src/domain/order_item/order_item_job_type';
import type { OrderItemStatusKey } from '@this/src/domain/order_item/order_item_status';
import { OrderItemStatusDone, OrderItemStatusOngoing } from '@this/src/domain/order_item/order_item_status';
import moment from 'moment';
import useOrganizationSelector from '../../../use_organization_selector';
import ItemsSearchArea from './items_search_area';
import ItemsResultTable from './items_result_table';
import type {
  InventoryStatus,
  AutoApprovedStatus,
  ShowMultipleHotelPlansStatus,
  SortKey,
  SortDirection
} from './types';

export type { InventoryStatus, AutoApprovedStatus, ShowMultipleHotelPlansStatus, SortKey, SortDirection };

interface Response {
  order_item_todos: HotelOrderItemEanTodoResponseArgs[];
  total_count: number;
  total_page: number;
}

const Items = observer(() => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [eanTodos, setEanTodos] = useState<HotelOrderItemEanTodo[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPage, setTotalPage] = useState(1);
  const [page, setPage] = useState(parseInt(utils.getParam('page') || '1', 10));

  const [inventoryStatus, setInventoryStatus] = useState<InventoryStatus>(
    (utils.getParam('inventory_status') as InventoryStatus) || 'not_yet'
  );
  const [autoApprovedStatus, setAutoApprovedStatus] = useState<AutoApprovedStatus>(
    (utils.getParam('auto_approved_status') as AutoApprovedStatus) || 'auto'
  );
  const [jobTypes, setJobTypes] = useState<OrderItemJobTypeKey[]>(
    (utils.getParam('job_types')?.split(',').map(Number) as OrderItemJobTypeKey[]) || OrderItemJobTypeNumKeys
  );
  const [statuses, setStatuses] = useState<OrderItemStatusKey[]>(
    (utils.getParam('statuses')?.split(',').map(Number) as OrderItemStatusKey[]) ||
      OrderItemStatusOngoing.concat(OrderItemStatusDone)
  );
  const [tripId, setTripId] = useState<number | undefined>(utils.getParamInt('trip_id') ?? undefined);
  const [startDateFrom, setStartDateFrom] = useState<Moment | undefined>(
    utils.getParamMoment('start_date_from') ?? undefined
  );
  const [startDateTo, setStartDateTo] = useState<Moment | undefined>(
    utils.getParamMoment('start_date_to') ?? undefined
  );
  const [receiveDateFrom, setReceiveDateFrom] = useState<Moment | undefined>(
    utils.getParamMoment('receive_date_from') ?? moment()
  );
  const [receiveDateTo, setReceiveDateTo] = useState<Moment | undefined>(
    utils.getParamMoment('receive_date_to') ?? undefined
  );
  const [organizationIds, renderOrganizationSelector] = useOrganizationSelector();
  const [logExists, setLogExists] = useState('');
  const [showMultipleHotelPlansStatus, setShowMultipleHotelPlansStatus] = useState<ShowMultipleHotelPlansStatus>(
    (utils.getParam('show_multiple_hotel_plans_status') as ShowMultipleHotelPlansStatus) || 'all'
  );
  const [sortKey, setSortKey] = useState<SortKey>((utils.getParam('sort_key') as SortKey) || 'started_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    (utils.getParam('sort_direction') as SortDirection) || 'asc'
  );
  const [showDescription, setShowDescription] = useState(false);
  const [isPaginate, setIsPaginate] = useState(true);
  const [isDiff, setIsDiff] = useState(utils.getParam('is_diff') === 'true');
  const [isNotDiff, setIsNotDiff] = useState(
    utils.getParam('is_not_diff') === undefined ? true : utils.getParam('is_not_diff') === 'true'
  );
  const fetchingRef = useRef(false);

  const fetchTodos = useCallback(() => {
    if (fetchingRef.current) {
      fetchingRef.current = false;
      return;
    }
    setIsLoading(true);
    setEanTodos([]);
    setTotalCount(0);
    setTotalPage(1);

    const params = {
      page,
      inventory_status: inventoryStatus,
      auto_approved_status: autoApprovedStatus,
      job_types: jobTypes,
      statuses,
      trip_id: tripId,
      start_date_from: startDateFrom?.format('YYYY-MM-DD'),
      start_date_to: startDateTo?.format('YYYY-MM-DD'),
      receive_date_from: receiveDateFrom?.format('YYYY-MM-DD'),
      receive_date_to: receiveDateTo?.format('YYYY-MM-DD'),
      organization_ids: organizationIds.join(','),
      log_exists: logExists,
      show_multiple_hotel_plans_status: showMultipleHotelPlansStatus,
      sort_key: sortKey,
      sort_direction: sortDirection,
      pagination: isPaginate ? 'true' : 'false',
      is_diff: isDiff ? 'true' : 'false',
      is_not_diff: isNotDiff ? 'true' : 'false'
    };
    _.each(params, (value, key) => {
      if (value === undefined) {
        delete params[key as keyof typeof params];
      }
    });
    const query = _.map(params, (value, key) => `${key}=${value}`).join('&');
    history.push(
      `/arrangement/todo_list?tab=AUTO_RESERVATION_INVENTORY&sub_tab=EAN_HOTEL&ean_hotel_type=ITEMS&${query}`
    );

    Fetcher.get<Response>('/arrangement/auto_reservation_inventory/ean_hotel/items.json', params)
      .then(res => {
        setEanTodos(res.order_item_todos.map(args => HotelOrderItemEanTodo.fromResponse(args)));
        setTotalCount(res.total_count);
        setTotalPage(res.total_page);
        if (res.total_page < page) {
          setPage(1);
        }
      })
      .catch(() => {
        Notification.error('データの取得に失敗しました');
      })
      .finally(() => setIsLoading(false));
  }, [
    page,
    inventoryStatus,
    autoApprovedStatus,
    jobTypes,
    statuses,
    tripId,
    startDateFrom,
    startDateTo,
    receiveDateFrom,
    receiveDateTo,
    organizationIds,
    logExists,
    showMultipleHotelPlansStatus,
    sortKey,
    sortDirection,
    isPaginate,
    isDiff,
    isNotDiff,
    history
  ]);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  return (
    <>
      <ItemsSearchArea
        jobTypes={jobTypes}
        statuses={statuses}
        autoApprovedStatus={autoApprovedStatus}
        inventoryStatus={inventoryStatus}
        tripId={tripId}
        startDateFrom={startDateFrom}
        startDateTo={startDateTo}
        receiveDateFrom={receiveDateFrom}
        receiveDateTo={receiveDateTo}
        showDescription={showDescription}
        logExists={logExists}
        showMultipleHotelPlansStatus={showMultipleHotelPlansStatus}
        sortKey={sortKey}
        sortDirection={sortDirection}
        isPaginate={isPaginate}
        isDiff={isDiff}
        isNotDiff={isNotDiff}
        setJobTypes={setJobTypes}
        setStatuses={setStatuses}
        setAutoApprovedStatus={setAutoApprovedStatus}
        setInventoryStatus={setInventoryStatus}
        setTripId={setTripId}
        setStartDateFrom={setStartDateFrom}
        setStartDateTo={setStartDateTo}
        setReceiveDateFrom={setReceiveDateFrom}
        setReceiveDateTo={setReceiveDateTo}
        setShowDescription={setShowDescription}
        setLogExists={setLogExists}
        setShowMultipleHotelPlansStatus={setShowMultipleHotelPlansStatus}
        setSortKey={setSortKey}
        setSortDirection={setSortDirection}
        setIsPaginate={setIsPaginate}
        setIsDiff={setIsDiff}
        setIsNotDiff={setIsNotDiff}
        renderOrganizationSelector={renderOrganizationSelector}
      />
      {isLoading ? (
        <Loading />
      ) : (
        <ItemsResultTable
          todos={eanTodos}
          totalCount={totalCount}
          totalPage={totalPage}
          page={page}
          showDescription={showDescription}
          isDiff={isDiff}
          isNotDiff={isNotDiff}
          fetchTodos={fetchTodos}
          setPage={setPage}
        />
      )}
    </>
  );
});

export default Items;
